import { useLayoutEffect, useMemo, useRef } from 'react';

import { computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { Editor, Range, ReactRenderer } from '@tiptap/react';
import { SuggestionOptions } from '@tiptap/suggestion';

import { workTypeOf } from '@/utils/promptWorkType';

import { CommandList, PromptMenuItems } from './CommandList';
import { insertPromptChip } from './insertSlashCommand';
import { templateChip } from './promptFill';
import { fitPromptPane } from './promptPaneRoom';
import {
  PromptChipInput,
  RefinedSuggestionItem,
  SuggestionItemGroup,
} from './SlashExtension.types';

let resizeObserver: ResizeObserver;
let intersectionObserver: IntersectionObserver;
let windowEventListener: () => void;

/** The composer box the menu lines up with; the editor's own element if none. */
const composerOf = (editor: Editor) =>
  (editor.view.dom.closest('[data-prompt-composer]') as HTMLElement | null) ??
  editor.view.dom;

/**
 * The menu sits above the composer, its left edge on the composer's, 8px
 * clear of it, fixed to the viewport: it flips below when there is no room
 * above, shifts to stay on screen, and takes its height from the room left
 * (the card's list scrolls inside it). Every open gets the same place.
 */
const updatePosition = (editor: Editor, element: HTMLElement) => {
  const doUpdate = (editor: Editor, element: HTMLElement) => {
    const anchor = composerOf(editor);
    computePosition(anchor, element, {
      placement: 'top-start',
      strategy: 'fixed',
      middleware: [
        offset(8),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          padding: 8,
          apply({ availableHeight, rects, elements }) {
            elements.floating.style.maxHeight = `${Math.max(
              200,
              availableHeight
            )}px`;
            // The card is as wide as the composer, up to 400px, so on a
            // phone it lines up with it instead of being shifted to fit.
            const menuWidth = Math.min(
              400,
              Math.max(280, rects.reference.width)
            );
            elements.floating.style.setProperty(
              '--prompt-menu-width',
              `${menuWidth}px`
            );
            fitPromptPane(
              elements.floating,
              elements.reference as Element,
              rects.reference.x + menuWidth
            );
          },
        }),
      ],
    }).then(({ x, y, strategy }) => {
      element.style.width = 'max-content';
      element.style.position = strategy;
      element.style.left = `${x}px`;
      element.style.top = `${y}px`;
    });
  };

  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  if (windowEventListener) {
    window.removeEventListener('resize', windowEventListener);
    window.removeEventListener('orientationchange', windowEventListener);
    window.removeEventListener('scroll', windowEventListener, true);
  }
  if (intersectionObserver) {
    intersectionObserver.disconnect();
  }

  if (editor.view.dom) {
    // The menu's own size changes as it is positioned (its height from the
    // room left, its width from the composer), so its observer waits for the
    // next frame: a synchronous re-position from inside the callback is the
    // "ResizeObserver loop" the browser stops.
    resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        try {
          if (editor.view.dom && element.isConnected) {
            doUpdate(editor, element);
          } else {
            resizeObserver.disconnect();
          }
        } catch {
          resizeObserver.disconnect();
        }
      });
    });

    windowEventListener = () => {
      doUpdate(editor, element);
    };

    window.addEventListener('resize', windowEventListener);
    window.addEventListener('orientationchange', windowEventListener);
    // Fixed to the viewport: a scroll anywhere moves the composer under it.
    window.addEventListener('scroll', windowEventListener, true);
    resizeObserver.observe(editor.view.dom);
    resizeObserver.observe(element);

    intersectionObserver = new IntersectionObserver(() => {
      doUpdate(editor, element);
    });
    intersectionObserver.observe(editor.view.dom);
  }
};

/** Switch on when any group carries resolved `templates`. */
export const hasPromptTemplates = (groups: SuggestionItemGroup[]) =>
  groups.some(group => Array.isArray(group.templates));

type RefinedGroup = RefinedSuggestionItem & {
  entries: RefinedSuggestionItem[];
};

/**
 * The `/` menu's options for the editor. They are built once and read the
 * latest groups through a ref, so the editor never has to be rebuilt (and lose
 * its chips) when the groups change.
 */
export function useSuggestions(
  suggestions: SuggestionItemGroup[],
  agentId?: number
) {
  const templatesOn = hasPromptTemplates(suggestions);

  const refinedSuggestions = useMemo<RefinedGroup[]>(() => {
    return suggestions.map((group, groupIndex) => {
      const groupKey = `g${group.id ?? groupIndex}-${groupIndex}`;
      const lucideIcon = group.lucide_icon || undefined;
      const icon = group.icon || undefined;
      // Switch on: the icon's tint, by the group's type of work. My prompts
      // (the personal pseudo-group) keeps the menu's ink.
      const workType =
        templatesOn && group.scope !== 'personal'
          ? workTypeOf(group.work_type)
          : undefined;

      // Switch on: templates (title shown, body sent; one with {variables}
      // opens the fill step first). Off: today's strings.
      const chips: PromptChipInput[] = templatesOn
        ? (group.templates ?? []).map(templateChip)
        : group.items;
      const mineFlags = templatesOn
        ? (group.templates ?? []).map(template => !!template.mine)
        : [];

      return {
        key: groupKey,
        label: group.label,
        icon,
        lucideIcon,
        workType,
        highlight: group.highlight,
        kind: 'group' as const,
        command: ({ editor, range }: { editor: Editor; range: Range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent(`/ ${group.highlight} `)
            .run();
        },
        entries: chips.map((chip, index) => {
          const title = typeof chip === 'string' ? chip : chip.title;
          return {
            key: `${groupKey}-${index}`,
            label: title,
            icon,
            lucideIcon,
            workType,
            highlight: title,
            kind: 'entry' as const,
            mine: mineFlags[index],
            groupLabel: group.label,
            chip,
            command: ({ editor, range }: { editor: Editor; range: Range }) => {
              insertPromptChip({ editor, chip, range });
            },
          };
        }),
      };
    });
  }, [suggestions, templatesOn]);

  const latest = useRef({ refinedSuggestions, templatesOn, agentId });
  useLayoutEffect(() => {
    latest.current = { refinedSuggestions, templatesOn, agentId };
  }, [refinedSuggestions, templatesOn, agentId]);

  return useMemo<Partial<SuggestionOptions>>(() => {
    return {
      items: ({ query }: { query: string; editor: Editor }) => {
        const { refinedSuggestions, templatesOn, agentId } = latest.current;
        let lowerCaseQuery = query.toLowerCase();

        // Trim only first leading space
        if (lowerCaseQuery.startsWith(' ')) {
          lowerCaseQuery = lowerCaseQuery.slice(1);
        }

        const matchingGroup = refinedSuggestions.find(
          group =>
            !!group.highlight &&
            lowerCaseQuery.startsWith(group.highlight.toLowerCase())
        );

        if (matchingGroup) {
          // Switch on: titles need not repeat the group word, so the text
          // after it filters. Switch off: today's filter on the whole query.
          const rest = lowerCaseQuery
            .slice(matchingGroup.highlight.length)
            .trim();
          const entries = matchingGroup.entries.filter(item => {
            const title = item.label.toLowerCase();
            if (!templatesOn) return title.includes(lowerCaseQuery);
            return (
              !rest || title.includes(rest) || title.includes(lowerCaseQuery)
            );
          });

          return {
            items: entries,
            groupLabel: matchingGroup.label,
            groupIcon: matchingGroup.icon,
            groupLucideIcon: matchingGroup.lucideIcon,
            groupWorkType: matchingGroup.workType,
            groupHighlight: matchingGroup.highlight,
            markQuery: templatesOn ? rest : lowerCaseQuery.trim(),
            agentId,
            templatesOn,
          } satisfies PromptMenuItems as any;
        }

        const groups = refinedSuggestions.filter(
          group =>
            group.label.toLowerCase().includes(lowerCaseQuery) ||
            group.highlight.toLowerCase().includes(lowerCaseQuery)
        );
        const typed = lowerCaseQuery.trim();
        // Switch on: the typed text also searches template titles across
        // every group, so `/invoice` finds "Invoice chase" without its group
        // word. Matching templates list after the groups, each with its
        // group. Switch off: today's groups-only filter.
        const titleMatches: RefinedSuggestionItem[] =
          templatesOn && typed
            ? refinedSuggestions.flatMap(group =>
                group.entries.filter(entry =>
                  entry.label.toLowerCase().includes(typed)
                )
              )
            : [];

        return {
          items: [...groups, ...titleMatches],
          markQuery: typed,
          // The list adds the caller's Recent row for this agent (#596).
          agentId,
          templatesOn,
        } satisfies PromptMenuItems as any;
      },

      render: () => {
        let component: ReactRenderer;

        return {
          onStart: props => {
            component = new ReactRenderer(CommandList, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            component.element.style.position = 'fixed';
            component.element.style.zIndex = '1500';
            component.element.className = 'prompt-menu-host';

            document.body.appendChild(component.element);

            updatePosition(props.editor, component.element);
          },

          onUpdate(props) {
            component.updateProps(props);

            if (!props.clientRect) {
              return;
            }

            updatePosition(props.editor, component.element);
          },

          onKeyDown(props) {
            if (props.event.key === 'Escape') {
              component.destroy();
              component.element.remove();

              return true;
            }

            return (component.ref as any)?.onKeyDown(props);
          },

          onExit() {
            component.destroy();
            component.element.remove();

            if (resizeObserver) {
              resizeObserver.disconnect();
            }
            if (windowEventListener) {
              window.removeEventListener('resize', windowEventListener);
              window.removeEventListener(
                'orientationchange',
                windowEventListener
              );
              window.removeEventListener('scroll', windowEventListener, true);
            }
            if (intersectionObserver) {
              intersectionObserver.disconnect();
            }
          },
        };
      },
    };
  }, []);
}
